import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRightLeft, CheckCircle, ExternalLink, FlaskConical, Play, Plus, Trash2, Loader2, XCircle, MoveRight, CalendarClock, MoreHorizontal, Download } from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "@/components/PageHeader";
import { ResponsiveTable } from "@/components/ResponsiveTable";
import { StatusBadge } from "@/components/StatusBadge";
import { MetricCard, MetricGrid } from "@/components/MetricCard";
import { Pagination } from "@/components/Pagination";
import { ScheduleDialog } from "@/components/ScheduleDialog";
import { NextRunsList } from "@/components/CountdownCell";
import { useConfirm } from "@/components/ConfirmDialog";
import { NewTestDialog } from "@/components/NewTestDialog";
import { MoveDialog } from "@/components/MoveDialog";
import { ImportTestFromUrlDialog } from "@/components/ImportTestFromUrlDialog";
import { useWorkspace } from "@/context/WorkspaceContext";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { api, formatApiError } from "@/lib/api";
import { formatDateTime } from "@/lib/datetime";
import { RefBadge } from "@/components/RefBadge";
import { StabilityChip } from "@/components/StabilityChip";

/** Test Cases page — shows ONLY tests that were created without being
 * attached to any project. These tests run against their stored target_url
 * directly (no env-base-URL substitution). To organise tests inside a
 * project/suite hierarchy, use the per-project Tests tab instead. */

function MoveTestDialog({ open, onOpenChange, test, projects, onMoved }) {
  const [projectId, setProjectId] = useState(test?.project_id || "");
  const [suiteId, setSuiteId] = useState(test?.suite_id || "");
  const [suites, setSuites] = useState([]);

  useEffect(() => {
    if (!projectId) return;
    api.get(`/projects/${projectId}/suites`)
      .then((r) => setSuites(r.data.items || []))
      .catch(() => setSuites([]));
  }, [projectId]);

  const handleMove = async () => {
    if (!suiteId) return;
    try {
      await api.put(`/tests/${test.id}/move`, { suite_id: suiteId });
      toast.success("Test moved");
      onMoved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[460px] rounded-xl border-[#E5E7EB]" data-testid="ws-move-test-dialog">
        <DialogHeader>
          <DialogTitle className="font-['Geist'] font-semibold tracking-tight">Move test</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-[#475569]">Destination project</label>
            <Select value={projectId} onValueChange={(v) => { setProjectId(v); setSuiteId(""); }}>
              <SelectTrigger className="rounded-md border-[#E5E7EB]" data-testid="ws-move-project-select">
                <SelectValue placeholder="Select a project" />
              </SelectTrigger>
              <SelectContent>
                {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-[#475569]">Destination suite</label>
            <Select value={suiteId} onValueChange={setSuiteId} disabled={!projectId}>
              <SelectTrigger className="rounded-md border-[#E5E7EB]" data-testid="ws-move-suite-select">
                <SelectValue placeholder={projectId ? "Select a suite" : "Pick a project first"} />
              </SelectTrigger>
              <SelectContent>
                {suites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <button onClick={() => onOpenChange(false)} className="h-9 px-4 text-sm font-medium border border-[#E5E7EB] rounded-md bg-white hover:bg-[#F8FAFC]">Cancel</button>
          <button onClick={handleMove} disabled={!suiteId || suiteId === test?.suite_id} data-testid="ws-move-submit" className="h-9 px-4 text-sm font-medium rounded-md bg-[#3B82F6] text-white hover:bg-[#2563EB] disabled:opacity-60">
            Move
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function WorkspaceTestsPage() {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [tests, setTests] = useState(null);
  const [total, setTotal] = useState(0);
  const [passing, setPassing] = useState(0);
  const [failing, setFailing] = useState(0);
  const [projects, setProjects] = useState([]);
  const [page, setPage] = useState(1);
  const PER_PAGE = 50;
  const [runningId, setRunningId] = useState(null);
  const [newOpen, setNewOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [moveOpen, setMoveOpen] = useState(false);
  const [movingTest, setMovingTest] = useState(null);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [schedulingTest, setSchedulingTest] = useState(null);
  const [allEnvironments, setAllEnvironments] = useState([]);
  const [schedulesByEntity, setSchedulesByEntity] = useState({});
  const [crossWsMovingTest, setCrossWsMovingTest] = useState(null);
  const { current } = useWorkspace();
  const canCrossWsMove = current?.is_personal === true && (current?.my_role === "owner" || current?.my_role === "editor");

  const loadActiveSchedules = useCallback(() => {
    api.get(`/schedules?status=active`).then((r) => {
      const grouped = {};
      for (const s of r.data.items || []) {
        if (s.entity_type !== "test") continue;
        if (!grouped[s.entity_id]) grouped[s.entity_id] = [];
        grouped[s.entity_id].push(s);
      }
      setSchedulesByEntity(grouped);
    }).catch(() => {});
  }, []);

  const loadProjects = useCallback(() => {
    api.get("/projects").then((r) => {
      const items = r.data.items || [];
      setProjects(items);
      // Test Cases (standalone) have no project of their own — schedule lets
      // the user pick any environment in the workspace. Fan-out fetch envs
      // across every project (cheap; envs are ~3-5 per project on average).
      Promise.all(
        items.map((p) => api.get(`/projects/${p.id}/environments`).then((r) => r.data.items || []).catch(() => []))
      ).then((lists) => setAllEnvironments(lists.flat()));
    }).catch(() => {});
  }, []);

  const loadTests = useCallback(() => {
    const params = new URLSearchParams({
      unassigned: "true",
      page: String(page),
      per_page: String(PER_PAGE),
    });
    api.get(`/tests?${params.toString()}`)
      .then((r) => {
        setTests(r.data.items || []);
        setTotal(r.data.total || 0);
        setPassing(r.data.passing || 0);
        setFailing(r.data.failing || 0);
      })
      .catch((e) => toast.error(formatApiError(e)));
  }, [page]);

  useEffect(loadProjects, [loadProjects]);
  useEffect(loadTests, [loadTests]);
  useEffect(loadActiveSchedules, [loadActiveSchedules]);

  const handleRun = async (t) => {
    setRunningId(t.id);
    try {
      const { data: run } = await api.post(`/tests/${t.id}/run`);
      navigate(`/runs/${run.id}`);
    } catch (e) {
      toast.error(formatApiError(e));
      setRunningId(null);
    }
  };

  const handleDelete = async (t) => {
    const ok = await confirm({
      title: "Move this test to the bin?",
      description: `"${t.name}" will be moved to the workspace recycle bin. You can restore it within 30 days.`,
      confirmLabel: "Move to bin",
      danger: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/tests/${t.id}`);
      toast.success("Test moved to bin");
      loadTests();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const columns = [
    {
      key: "name",
      label: "Test name",
      mobileVariant: "title",
      render: (t) => (
        <div className="flex flex-col">
          <div className="flex items-center gap-2 flex-wrap">
            <RefBadge value={t.ref} testId={`ws-test-ref-${t.id}`} />
            <StabilityChip steps={t.steps} testId={`ws-test-stability-${t.id}`} compact />
          </div>
          <Link to={`/tests/${t.id}`} data-testid={`ws-test-name-link-${t.id}`} className="rt-primary hover:text-[#3B82F6] transition-colors mt-1">
            {t.name}
          </Link>
        </div>
      ),
    },
    {
      key: "target",
      label: "Target URL",
      render: (t) => (
        <span className="rt-value text-xs text-[#64748B] inline-flex items-center gap-1 truncate font-mono">
          {t.target_url.replace(/^https?:\/\//, "").slice(0, 36)}
          <ExternalLink size={10} />
        </span>
      ),
    },
    { key: "steps", label: "Steps", render: (t) => <span className="text-[#334155]">{t.steps.length}</span> },
    {
      key: "last_run",
      label: "Last run",
      render: (t) => (
        <div className="flex flex-col gap-0">
          {t.last_run ? (
            <Link to={`/runs/${t.last_run.id}`} className="inline-flex flex-col gap-0.5">
              <StatusBadge status={t.last_run.status} />
              <span className="text-[11px] text-[#94A3B8]" data-testid={`ws-test-last-run-at-${t.id}`}>
                {t.last_run.started_at ? formatDateTime(t.last_run.started_at) : ""}
              </span>
            </Link>
          ) : (
            <StatusBadge status={null} />
          )}
        </div>
      ),
    },
    {
      key: "updated",
      label: "Updated",
      render: (t) => <span className="text-xs text-[#64748B]">{formatDateTime(t.updated_at)}</span>,
    },
    {
      key: "actions",
      label: "Actions",
      align: "right",
      mobileVariant: "actions",
      render: (t) => {
        const upcoming = schedulesByEntity[t.id] || [];
        return (
          <div className="flex flex-col items-end">
            <div className="inline-flex flex-col gap-1.5">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleRun(t)}
                  disabled={runningId === t.id}
                  data-testid={`ws-run-test-${t.id}`}
                  className="inline-flex items-center gap-1.5 px-3 h-8 rounded-md bg-[#3B82F6] text-white text-sm font-medium hover:bg-[#2563EB] transition-colors disabled:opacity-50"
                >
                  {runningId === t.id ? <Loader2 size={12} className="animate-spin" /> : <Play size={12} />}
                  Run test
                </button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      data-testid={`ws-test-row-actions-${t.id}`}
                      aria-label="Row actions"
                      className="inline-flex items-center justify-center w-8 h-8 rounded-md border border-[#E5E7EB] bg-white hover:bg-[#F8FAFC]"
                    >
                      <MoreHorizontal size={14} className="text-[#64748B]" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="rounded-lg border-[#E5E7EB] p-1 w-52">
                    <DropdownMenuItem
                      onClick={() => { setSchedulingTest(t); setScheduleOpen(true); }}
                      data-testid={`ws-schedule-test-${t.id}`}
                      className="rounded-md px-2 py-2 text-sm cursor-pointer focus:bg-[#F1F5F9]"
                    >
                      <CalendarClock size={13} className="mr-2 text-[#3B82F6]" />
                      Schedule…
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => { setMovingTest(t); setMoveOpen(true); }}
                      data-testid={`ws-move-test-${t.id}`}
                      className="rounded-md px-2 py-2 text-sm cursor-pointer focus:bg-[#F1F5F9]"
                    >
                      <MoveRight size={13} className="mr-2 text-[#64748B]" />
                      Move to another project / suite…
                    </DropdownMenuItem>
                    {canCrossWsMove && (
                      <DropdownMenuItem
                        onClick={() => setCrossWsMovingTest(t)}
                        data-testid={`ws-cross-move-test-${t.id}`}
                        className="rounded-md px-2 py-2 text-sm cursor-pointer focus:bg-[#F1F5F9]"
                      >
                        <ArrowRightLeft size={13} className="mr-2 text-[#475569]" />
                        Move to workspace…
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => handleDelete(t)}
                      data-testid={`ws-delete-test-${t.id}`}
                      className="rounded-md px-2 py-2 text-sm cursor-pointer text-[#B91C1C] focus:bg-[#FEF2F2]"
                    >
                      <Trash2 size={13} className="mr-2" />
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
              {upcoming.length > 0 && (
                <div data-testid={`ws-test-next-wrapper-${t.id}`}>
                  <NextRunsList schedules={upcoming} testIdPrefix={`ws-test-next-${t.id}`} />
                </div>
              )}
            </div>
          </div>
        );
      },
    },
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 py-8 sm:py-10" data-testid="workspace-tests-page">
      <PageHeader
        title="Test Cases"
        description="Standalone tests not linked to any project — they run against the URL stored on the test itself."
        actions={
          <div className="flex items-center gap-2">
            <button
              onClick={() => setImportOpen(true)}
              data-testid="ws-import-test-button"
              className="inline-flex items-center gap-2 border border-[#CBD5E1] bg-white text-[#334155] px-4 h-10 rounded-md text-sm font-medium hover:bg-[#F1F5F9] transition-colors"
              title="Copy a test case from another Probe deployment (e.g. production) into this workspace"
            >
              <Download size={15} />
              Copy from URL
            </button>
            <button
              onClick={() => setNewOpen(true)}
              data-testid="ws-new-test-button"
              className="inline-flex items-center gap-2 bg-[#3B82F6] text-white px-4 h-10 rounded-md text-sm font-medium hover:bg-[#2563EB] transition-colors"
            >
              <Plus size={15} />
              Create test case
            </button>
          </div>
        }
      />

      <MetricGrid columns={3}>
        <MetricCard testId="ws-stat-total-tests" label="Test Cases" icon={FlaskConical} value={total} />
        <MetricCard testId="ws-stat-passing" label="Passing" icon={CheckCircle} value={passing} />
        <MetricCard testId="ws-stat-failing" label="Failing" icon={XCircle} value={failing} />
      </MetricGrid>

      {tests === null ? (
        <div className="surface-card p-6 text-sm text-[#64748B]">Loading…</div>
      ) : tests.length === 0 ? (
        <div className="surface-card p-12 text-center" data-testid="ws-empty-tests-message">
          <FlaskConical size={24} className="mx-auto mb-3 text-[#94A3B8]" />
          <h2 className="font-['Geist'] font-semibold text-lg text-[#0F172A]">No test cases yet</h2>
          <p className="text-sm text-[#64748B] mt-2 max-w-md mx-auto">Create an independent test case here, or open a project to create tests within a project's suites.</p>
          <button onClick={() => setNewOpen(true)} className="mt-6 inline-flex items-center gap-2 bg-[#3B82F6] text-white px-4 h-10 rounded-md text-sm font-medium hover:bg-[#2563EB]">
            <Plus size={15} />
            Create test case
          </button>
        </div>
      ) : (
        <>
          <ResponsiveTable
            testId="ws-tests-table"
            columns={columns}
            rows={tests}
            getRowTestId={(t) => `ws-test-row-${t.id}`}
          />
          <Pagination
            page={page}
            perPage={PER_PAGE}
            total={total}
            onChange={setPage}
            testId="ws-tests-pagination"
          />
        </>
      )}

      <NewTestDialog
        key={`ws-new-test-${newOpen}`}
        open={newOpen}
        onOpenChange={setNewOpen}
        projectId={null}
        suites={[]}
        environments={[]}
      />

      <ImportTestFromUrlDialog
        key={`ws-import-${importOpen}`}
        open={importOpen}
        onOpenChange={setImportOpen}
        onImported={() => loadTests()}
      />

      <MoveTestDialog
        key={`ws-move-${movingTest?.id || "none"}-${moveOpen}`}
        open={moveOpen}
        onOpenChange={setMoveOpen}
        test={movingTest}
        projects={projects}
        onMoved={loadTests}
      />

      <ScheduleDialog
        key={`ws-schedule-${schedulingTest?.id || "none"}-${scheduleOpen}`}
        open={scheduleOpen}
        onOpenChange={setScheduleOpen}
        entityType="test"
        entityId={schedulingTest?.id}
        entityName={schedulingTest?.name}
        environments={allEnvironments}
        defaultEnvironmentId={allEnvironments[0]?.id}
      />

      {crossWsMovingTest && (
        <MoveDialog
          open={!!crossWsMovingTest}
          onOpenChange={(o) => !o && setCrossWsMovingTest(null)}
          kind="test"
          entity={crossWsMovingTest}
          onMoved={loadTests}
        />
      )}
    </div>
  );
}
